<template>
  <div class="team-analysis-view">
    <!-- 第一列：选择面板 -->
    <div class="selection-column">
      <!-- 联赛选择 -->
      <div class="panel league-panel">
        <h3>Leagues</h3>
        <div v-for="country in countries" :key="country.id"
             class="flag-container"
             :class="{ active: selectedOption.league === country.id }"
             @click="selectLeague(country)">
          <span>{{ country.code }}</span>
          <span>{{ country.league_name }}</span>
        </div>
      </div>

      <!-- 队伍列表 -->
      <div class="panel team-list-panel" v-if="selectedOption.league">
        <div class="panel-header">
          <h3>Teams</h3>
        </div>
        <div v-if="loadingTeams">Loading teams...</div>
        <div v-if="teamsError">{{ teamsError }}</div>
        <div v-else class="team-list">
          <div v-for="team in teams" :key="team.id"
               class="team-item"
               :class="{ active: selectedOption.team?.id === team.id }"
               @click="selectTeam(team)">
            <span>{{ team.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 第二列：队伍详情 -->
    <div class="details-column" v-if="selectedOption.team">
      <div class="panel team-details-panel">
        <h3>{{ selectedOption.team.name }}</h3>
        <div v-for="file in commonFiles" :key="file" class="visual-item">
          <template v-if="file.endsWith('.png')">
            <img :src="visualPath(selectedOption.team.slug, file)" :alt="file" class="visual-image" />
          </template>
          <template v-else-if="file.endsWith('.html')">
            <iframe :src="visualPath(selectedOption.team.slug, file)" class="visual-frame"></iframe>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { fetchCountries, fetchTeams } from '@/api/client.js'

const countries = ref([])
const teams = ref([])
const loadingTeams = ref(false)
const teamsError = ref(null)

const selectedOption = ref({
  league: null,
  team: null
})

const commonFiles = [
  "age_distribution.png",
  "age_vs_match_played.png",
  "average_yellow_cards_by_Name.png",
  "average_yellow_cards_by_Nation.png",
  "average_yellow_cards_by_Position.png",
  "player_hours_treemap.html",
  "player_match_startXI_distribution.png",
  "yellow_cards_pie_chart.png"
]

const visualPath = (teamSlug, file) => {
  // 获取媒体基础URL
  const mediaBaseUrl = import.meta.env.VITE_MEDIA_BASE_URL || 'http://localhost:8000/';
  return `${mediaBaseUrl}team-visuals/${teamSlug}/${file}`;
};

const fetchCountriesData = async () => {
  try {
    countries.value = await fetchCountries()
    // Set the first country as default selection
    if (countries.value.length > 0) {
      selectedOption.value.league = countries.value[0].id
      fetchTeamsData(countries.value[0].id)
    }
  }
  catch(e) { console.error(e) }
}

const fetchTeamsData = async (countryId) => {
  loadingTeams.value = true
  teamsError.value = null
  teams.value = []
  selectedOption.value.team = null
  try {
    teams.value = await fetchTeams(countryId)
  }
  catch(e) {
    teamsError.value = `Failed to fetch teams: ${e.message}`
  }
  finally {
    loadingTeams.value = false
  }
}

const selectLeague = (country) => {
  selectedOption.value.league = country.id
  fetchTeamsData(country.id)
}

const selectTeam = (team) => selectedOption.value.team = team

onMounted(async () => await fetchCountriesData())
watch(selectedOption.value, (newVal, oldVal) => {
  if (newVal.league && newVal.league !== oldVal?.league) fetchTeamsData(newVal.league)
})
</script>

<style scoped>
.team-analysis-view {
  display: flex;
  gap: 20px;
  height: calc(100vh - 120px);
  transition: all 0.3s ease;
}

.selection-column {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
}

.details-column {
  flex: 2;
  overflow-y: auto;
}

.panel {
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #f9f9f9;
  overflow-y: auto;
  transition: all 0.3s ease;
}

.league-panel {
  flex: 0 0 auto;
}

.team-list-panel {
  flex: 1;
}

.flag-container, .team-item {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 8px;
  border-radius: 5px;
}

.flag-container:hover, .team-item:hover { background-color: #eef; }
.flag-container.active, .team-item.active { background-color: #dde; font-weight: bold; }
.flag-container img {
  height: 2em;
  width: 2.5em;
  object-fit: contain;
  display: block;
}

.team-logo {
  width: 25px;
  height: 25px;
  object-fit: contain;
}

.details-logo {
  max-width: 150px;
  margin: 20px auto;
  display: block;
}

.visual-image {
  max-width: 100%;
  height: auto;
  margin: 10px 0;
  border-radius: 5px;
}

.visual-frame {
  width: 100%;
  height: 500px;
  border: none;
  margin: 10px 0;
  border-radius: 5px;
}

/* 移动端适配 */
@media (max-width: 767px) {
  .team-analysis-view {
    flex-direction: column;
    height: auto;
  }

  .selection-column, .details-column {
    flex: 1 1 100%;
  }

  .panel {
    padding: 10px;
  }
}
</style>
